import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { Button } from "../components/coreUI/button";
import { StripePlanType } from "./StripeCheckout";

export default function Stripe() {
  const [selectedPlan, setSelectedPlan] = useState<StripePlanType | null>(null);
  const navigate = useNavigate();

  const handleSubscriptionClick = (planType: StripePlanType) => {
    setSelectedPlan(planType);
    // Store selected plan in sessionStorage for checkout process
    sessionStorage.setItem("selectedPlan", planType);
    console.log(`Selected plan: ${planType}`);

    // Navigate to Stripe checkout
    setTimeout(() => {
      navigate("/stripe-checkout");
    }, 500); // Small delay to show selection feedback
  };

  return (
    <div className="min-w-screen w-screen min-h-screen h-full flex flex-col bg-surface-one">
      <div className="flex-1 flex flex-col items-center justify-center px-2 py-2">
        <div className="w-full max-w-4xl h-full flex flex-col grow bg-white rounded-2xl">
          <div className="grow mx-auto flex flex-col items-center justify-center mt-8 mb-8 px-4 gap-8">
            {/* Header Section */}
            <div className="flex flex-col items-center justify-center text-center">
              <p className="text-sm uppercase text-brand-primary">
                Choose Your Plan
              </p>
              <p className="text-4xl text-foreground-primary">
                Stripe Checkout
              </p>
              <p className="text-4xl font-bold text-foreground-primary">
                Subscription Plans
              </p>
              <p className="text-lg text-foreground-secondary mt-4 max-w-2xl">
                Select the perfect plan for your health journey. All plans
                include access to our comprehensive health tracking platform.
              </p>
              <p className="text-sm text-foreground-secondary mt-2 opacity-75">
                💡 Click on any card to select your plan
              </p>
            </div>

            {/* Subscription Plans */}
            <div className="flex flex-col lg:flex-row items-center justify-center gap-6 w-full max-w-5xl">
              {/* Plan 1: Device OTP + Monthly App */}
              <div
                className={`flex flex-col items-center p-6 border rounded-xl bg-white shadow-sm hover:shadow-lg transition-all duration-200 w-full lg:w-1/3 cursor-pointer ${
                  selectedPlan === "device-otp-monthly"
                    ? "border-brand-primary ring-2 ring-brand-primary/20"
                    : "border-gray-200 hover:border-brand-primary/50"
                }`}
                onClick={() => handleSubscriptionClick("device-otp-monthly")}
              >
                <div className="text-center mb-6">
                  <h3 className="text-xl font-semibold text-foreground-primary mb-2">
                    Nettle + App Monthly
                  </h3>
                  <p className="text-sm text-foreground-secondary mb-4">
                    Device OTP + Monthly App Subscription
                  </p>
                  <div className="text-3xl font-bold text-brand-primary mb-2">
                    $499
                    <span className="text-sm font-normal">
                      {" "}
                      one-time payment
                    </span>
                  </div>
                  <p className="text-xs text-foreground-secondary mt-1">
                    12 months for free, then $2.99 per month
                  </p>
                </div>

                <div className="flex flex-col gap-2 mb-6 text-sm text-foreground-secondary">
                  <div>✓ Nettle is yours immediately</div>
                  <div>✓ Monthly App Access</div>
                  <div>✓ Health Tracking</div>
                  <div>✓ 12 months free</div>
                </div>

                <Button
                  variant={
                    selectedPlan === "device-otp-monthly"
                      ? "primary"
                      : "secondary"
                  }
                  size="default"
                  fullWidth
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSubscriptionClick("device-otp-monthly");
                  }}
                  className="font-semibold"
                >
                  {selectedPlan === "device-otp-monthly"
                    ? "✓ Selected"
                    : "Select Monthly Plan"}
                </Button>
              </div>

              {/* Plan 2: Device OTP + Yearly App */}
              <div
                className={`flex flex-col items-center p-6 border rounded-xl bg-white shadow-sm hover:shadow-lg transition-all duration-200 w-full lg:w-1/3 relative cursor-pointer ${
                  selectedPlan === "device-otp-yearly"
                    ? "border-brand-primary ring-2 ring-brand-primary/20"
                    : "border-gray-200 hover:border-brand-primary/50"
                }`}
                onClick={() => handleSubscriptionClick("device-otp-yearly")}
              >
                {/* Popular Badge */}
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-brand-primary text-white px-4 py-1 rounded-full text-xs font-medium">
                  Most Popular
                </div>

                <div className="text-center mb-6 pt-2">
                  <h3 className="text-xl font-semibold text-foreground-primary mb-2">
                    Nettle + App Yearly
                  </h3>
                  <p className="text-sm text-foreground-secondary mb-4">
                    Device OTP + Yearly App Subscription
                  </p>
                  <div className="text-3xl font-bold text-brand-primary mb-2">
                    $499
                    <span className="text-sm font-normal">
                      {" "}
                      one-time payment
                    </span>
                  </div>
                  <p className="text-xs text-green-600 font-medium">
                    Save 7% vs Monthly Plan
                  </p>
                  <p className="text-xs text-foreground-secondary mt-1">
                    12 months for free, then $24.99 per year
                  </p>
                </div>

                <div className="flex flex-col gap-2 mb-6 text-sm text-foreground-secondary">
                  <div>✓ Nettle is yours immediately</div>
                  <div>✓ Yearly App Access</div>
                  <div>✓ Health Tracking</div>
                  <div>✓ 12 months free</div>
                </div>

                <Button
                  variant={
                    selectedPlan === "device-otp-yearly"
                      ? "primary"
                      : "secondary"
                  }
                  size="default"
                  fullWidth
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSubscriptionClick("device-otp-yearly");
                  }}
                  className="font-semibold"
                >
                  {selectedPlan === "device-otp-yearly"
                    ? "✓ Selected"
                    : "Select Yearly Plan"}
                </Button>
              </div>

              {/* Plan 3: All-in-One Monthly */}
              <div
                className={`flex flex-col items-center p-6 border rounded-xl bg-white shadow-sm hover:shadow-lg transition-all duration-200 w-full lg:w-1/3 cursor-pointer ${
                  selectedPlan === "all-in-one-monthly"
                    ? "border-brand-primary ring-2 ring-brand-primary/20"
                    : "border-gray-200 hover:border-brand-primary/50"
                }`}
                onClick={() => handleSubscriptionClick("all-in-one-monthly")}
              >
                <div className="text-center mb-6">
                  <h3 className="text-xl font-semibold text-foreground-primary mb-2">
                    All-in-One Monthly
                  </h3>
                  <p className="text-sm text-foreground-secondary mb-4">
                    Device + App Monthly Subscription
                  </p>
                  <div className="text-3xl font-bold text-brand-primary mb-2">
                    $49.99<span className="text-sm font-normal">/month</span>
                  </div>
                </div>

                <div className="flex flex-col gap-2 mb-6 text-sm text-foreground-secondary">
                  <div>✓ Device Included</div>
                  <div>✓ Health Tracking</div>
                  <div>✓ Device Replacement</div>
                  <div>✓ 24/7 Support</div>
                </div>

                <Button
                  variant={
                    selectedPlan === "all-in-one-monthly"
                      ? "primary"
                      : "secondary"
                  }
                  size="default"
                  fullWidth
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSubscriptionClick("all-in-one-monthly");
                  }}
                  className="font-semibold"
                >
                  {selectedPlan === "all-in-one-monthly"
                    ? "✓ Selected"
                    : "Select All-in-One"}
                </Button>
              </div>
            </div>

            {/* Selected Plan Info */}
            {selectedPlan && (
              <div className="mt-6 p-4 bg-brand-primary/10 rounded-lg text-center">
                <p className="text-sm text-foreground-secondary">
                  Selected:{" "}
                  <span className="font-semibold text-brand-primary">
                    {selectedPlan === "device-otp-monthly" &&
                      "Nettle + App Monthly"}
                    {selectedPlan === "device-otp-yearly" &&
                      "Nettle + App Yearly"}
                    {selectedPlan === "all-in-one-monthly" &&
                      "All-in-One Monthly"}
                  </span>
                </p>
                <p className="text-xs text-foreground-secondary mt-1">
                  Redirecting to secure checkout...
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
